import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Star, ArrowRight, Sparkles } from 'lucide-react';
import SEO from '@/components/dubinko/SEO';
import { REVIEWS } from '@/constants/testIds';

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reviews').then(({ data }) => setReviews(data)).finally(() => setLoading(false));
  }, []);

  const avg = reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : 0;

  return (
    <main data-testid={REVIEWS.page}>
      <SEO
        title="Recenzije klijenata"
        description="Pravi utisci klijenata Dubinko-a — dubinsko čišćenje stanova, kuća, poslovnih prostora i tepiha u Nišu."
        path="/recenzije"
        schema={reviews.length ? {
          '@context': 'https://schema.org',
          '@type': 'CleaningService',
          name: 'Dubinko',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: avg.toFixed(1),
            reviewCount: reviews.length,
          },
        } : null}
      />

      <section className="section-pad aurora-bg grain">
        <div className="container-x">
          <span className="tag-pill">Iskustva klijenata</span>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-4 max-w-3xl">
            Recenzije i utisci
          </h1>
          {reviews.length > 0 && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
              <div className="flex gap-0.5 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < Math.round(avg) ? 'fill-amber-500' : ''}`} />
                ))}
              </div>
              <div className="font-display font-bold text-xl">{avg.toFixed(1)} / 5</div>
              <div className="text-sm text-slate-500">na osnovu {reviews.length} {reviews.length === 1 ? 'recenzije' : 'recenzija'}</div>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          {loading ? (
            <div className="text-slate-500">Učitavanje…</div>
          ) : reviews.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
              <Sparkles className="w-10 h-10 mx-auto text-purple-600" />
              <h2 className="font-display text-2xl font-bold mt-3">Još uvek nema recenzija</h2>
              <p className="text-slate-600 mt-2">Budite prvi koji deli iskustvo — zakažite čišćenje i ostavite utisak.</p>
              <Link to="/rezervacija" className="btn-primary mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold">
                Zakaži <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <article key={r.id} data-testid={REVIEWS.card}
                  className="bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-lg transition">
                  <div className="flex gap-0.5 text-amber-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-amber-500' : ''}`} />
                    ))}
                  </div>
                  <p className="text-slate-700 mt-4 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                  <div className="mt-5 text-sm">
                    <div className="font-display font-bold text-slate-900">{r.customer_name}</div>
                    <div className="text-slate-500">{new Date(r.created_at).toLocaleDateString('sr-RS')}</div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
